import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Page, Button, EditIcon } from '@occc-app/design-system';
import {
  RetailerNetworkLocationGroupFragment,
  RetailerNetworkLocationFilter,
  RetailerNetworkLocationsWithPerformanceExcerptFilter,
  Retailer_Network_Performance_Period,
  RetailerNetworkFragment,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkLocationGroups } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { RetailerNetworkLocationGroupLocationsTableContainer } from './retailer-network-location-group-locations-table.container';
import { RetailerNetworkLocationGroupAddLocationsDialog } from './components/retailer-network-location-group-add-location-dialog.component';
import { RetailerNetworkLocationGroupRemoveLocationDialog } from './components/retailer-network-location-group-remove-location-dialog.component';
import RetailerNetworkLocationGroupUsersTable from './components/retailer-network-location-group-users-table';

import { LocationGroupUpdateDialog } from '../components/location-group-update-dialog/location-group-update-dialog.component';
import RetailerNetworkMonthlyGoals from '../components/retailer-network-monthly-goals/retailer-network-monthly-goals.container';
import RetailerNetworkPerformance from '../components/retailer-network-performance';
import { routeCreator } from '../retailer-connect.utils';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

type RetailerNetworkLocationGroupDetailsPageProps = {
  locationGroup: RetailerNetworkLocationGroupFragment;
  retailerNetwork: RetailerNetworkFragment;
  addLocationsToLocationGroup: (locationUuids: string[]) => Promise<void>;
  removeLocationFromLocationGroup: (locationUuid: string) => Promise<void>;
  isRemoveLocationFromGroupInProgress: boolean;
  isAddLocationToLocationGroupInProgress: boolean;
};

export const RetailerNetworkLocationGroupDetailsPage = ({
  locationGroup,
  retailerNetwork,
  addLocationsToLocationGroup,
  removeLocationFromLocationGroup,
  isRemoveLocationFromGroupInProgress,
  isAddLocationToLocationGroupInProgress,
}: RetailerNetworkLocationGroupDetailsPageProps) => {
  const navigate = useNavigate();
  const [addLocationsDialogOpen, setAddLocationsDialogOpen] = useState<boolean>(false);
  const [removeLocationDialogOpen, setRemoveLocationDialogOpen] = useState<boolean>(false);
  const [editLocationGroupDialogOpen, setEditLocationGroupDialogOpen] = useState<boolean>(false);
  const [locationToRemove, setLocationToRemove] = useState<string | null>(null);
  const { isRetailerNetworkSiteUser } = useRetailerNetworkUserSelector();

  const retailerNetworkLocationsFilter: Omit<RetailerNetworkLocationFilter, 'search'> = {
    retailerNetworkUuidEquals: retailerNetwork.uuid,
  };

  const retailerNetworkLocationGroupLocationsFilter: Omit<
    RetailerNetworkLocationsWithPerformanceExcerptFilter,
    'search'
  > = {
    retailerNetworkUuidEquals: retailerNetwork.uuid,
    locationGroupUuidEquals: locationGroup.uuid,
    periodEquals: Retailer_Network_Performance_Period.ThisMonth,
  };

  const onAddLocationsToLocationGroup = async (locationUuids: string[]) => {
    await addLocationsToLocationGroup(locationUuids);
    setAddLocationsDialogOpen(false);
  };
  const onRemoveLocationFromGroup = async () => {
    if (locationToRemove) {
      await removeLocationFromLocationGroup(locationToRemove);
    }

    setLocationToRemove(null);
    setRemoveLocationDialogOpen(false);
  };

  const handleRemoveLocationDialogOpen = (locationUuid: string | null) => {
    setLocationToRemove(locationUuid);
    setRemoveLocationDialogOpen(true);
  };
  const handleEditLocationGroupDialogOpen = () => {
    setEditLocationGroupDialogOpen(!editLocationGroupDialogOpen);
  };
  const handleOpenLocationDetailsPage = useCallback(
    (locationUuid: string) => {
      navigate(routeCreator.locationDetails(retailerNetwork.uuid, locationUuid));
    },
    [navigate, retailerNetwork.uuid],
  );

  return (
    <Page
      browserTabTitle='Retailer Network Location Group Details'
      pageHeaderProps={{ title: locationGroup.name, subtitle: 'Location Group' }}
      containerProps={{ variant: 'md' }}
      actions={
        <Can I={UpdateRetailerNetworkLocationGroups({ uuid: retailerNetwork.uuid })}>
          <Button
            variant='contained'
            color='secondary'
            startIcon={<EditIcon />}
            onClick={handleEditLocationGroupDialogOpen}
          >
            Edit
          </Button>
        </Can>
      }
    >
      <RetailerNetworkMonthlyGoals
        filter={{
          locationGroupUuidEquals: locationGroup.uuid,
          retailerNetworkUuidEquals: retailerNetwork.uuid,
        }}
      />

      {locationGroup.countOfLocations ? (
        <RetailerNetworkPerformance
          title='Location Group Performance'
          filter={{
            retailerNetworkUuidEquals: retailerNetwork.uuid,
            locationGroupUuidEquals: locationGroup.uuid,
          }}
          scope='locationGroup'
          retailerNetworkGoals={{
            membersPerStore: retailerNetwork.monthlyPerformanceGoal.membersPerStore,
            transactionsPerStore: retailerNetwork.monthlyPerformanceGoal.transactionsPerStore,
          }}
        />
      ) : null}

      <RetailerNetworkLocationGroupLocationsTableContainer
        retailerNetworkUuid={retailerNetwork.uuid}
        filter={retailerNetworkLocationGroupLocationsFilter}
        setAddLocationsDialogOpen={setAddLocationsDialogOpen}
        setRemoveLocationDialogOpen={handleRemoveLocationDialogOpen}
        handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
      />

      {!isRetailerNetworkSiteUser && (
        <RetailerNetworkLocationGroupUsersTable
          retailerNetworkUuid={retailerNetwork.uuid}
          locationGroup={locationGroup}
        />
      )}

      {addLocationsDialogOpen && (
        <RetailerNetworkLocationGroupAddLocationsDialog
          open={addLocationsDialogOpen}
          setOpen={setAddLocationsDialogOpen}
          filter={retailerNetworkLocationsFilter}
          onAddLocationsToLocationGroup={onAddLocationsToLocationGroup}
          loading={isAddLocationToLocationGroupInProgress}
          retailerNetworkLocationGroupUuid={locationGroup.uuid}
        />
      )}
      {removeLocationDialogOpen && (
        <RetailerNetworkLocationGroupRemoveLocationDialog
          open={removeLocationDialogOpen}
          setOpen={setRemoveLocationDialogOpen}
          onConfirm={onRemoveLocationFromGroup}
          loading={isRemoveLocationFromGroupInProgress}
        />
      )}
      {editLocationGroupDialogOpen && (
        <LocationGroupUpdateDialog
          locationGroup={locationGroup}
          open={editLocationGroupDialogOpen}
          onClose={handleEditLocationGroupDialogOpen}
          retailerNetworkUuid={retailerNetwork.uuid}
        />
      )}
    </Page>
  );
};
